/**
 * Collection Filtering — handles level tabs, inline filter chips, sort,
 * mobile filter drawer, and desktop chip dropdowns.
 * Uses Shopify Section Rendering API for seamless page updates.
 */
class CollectionFiltering {
  constructor() {
    this.container = document.getElementById('collection-page');
    this.productGrid = document.getElementById('product-grid');
    this.skeletonGrid = document.getElementById('skeleton-grid');
    this.sectionId = this.container?.dataset.sectionId;

    if (!this.container) return;

    this.priceDebounceTimer = null;
    this.init();
  }

  init() {
    this.bindLevelTabs();
    this.bindFilters();
    this.bindSort();
    this.bindFilterToggle();
    this.bindFilterChips();
    // pathname + search, not href: section_id is appended to this URL, and after
    // a #fragment the server would never receive it.
    window.addEventListener('popstate', () => {
      this.fetchAndRender(window.location.pathname + window.location.search, { pushHistory: false });
    });

    // Close chip dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-filter-chip]')) {
        this.closeAllChipDropdowns();
      }
    });
  }

  /* ===== LEVEL TABS ===== */
  bindLevelTabs() {
    this.container.querySelectorAll('[data-level-tab]').forEach(tab => {
      tab.addEventListener('click', (e) => {
        const url = e.currentTarget.dataset.levelUrl;
        this.fetchAndRender(url);
      });
    });
  }

  /* ===== INLINE FILTER CHIP DROPDOWNS (desktop) ===== */
  bindFilterChips() {
    this.container.querySelectorAll('[data-chip-toggle]').forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const chip = toggle.closest('[data-filter-chip]');
        const dropdown = chip.querySelector('[data-chip-dropdown]');
        const isOpen = dropdown.classList.contains('is-open');

        // Close all other dropdowns first
        this.closeAllChipDropdowns();

        if (!isOpen) {
          // Position dropdown below the toggle button using fixed positioning
          const rect = toggle.getBoundingClientRect();
          dropdown.style.top = (rect.bottom + 8) + 'px';
          dropdown.style.left = rect.left + 'px';
          dropdown.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  closeAllChipDropdowns() {
    this.container.querySelectorAll('[data-chip-dropdown].is-open').forEach(dd => {
      dd.classList.remove('is-open');
    });
    this.container.querySelectorAll('[data-chip-toggle]').forEach(toggle => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  }

  /* ===== FILTERS ===== */
  bindFilters() {
    this.container.querySelectorAll('[data-filter-input]').forEach(input => {
      if (input.type === 'number') {
        input.addEventListener('input', () => this.syncPairedInputs(input));
      } else {
        input.addEventListener('change', () => {
          this.syncPairedInputs(input);
          const url = this.buildFilterUrl();
          this.fetchAndRender(url);
        });
      }
    });

    // Price apply button
    this.container.querySelectorAll('[data-price-apply]').forEach(btn => {
      btn.addEventListener('click', () => {
        const url = this.buildFilterUrl();
        this.fetchAndRender(url);
      });
    });
  }

  // Each filter renders twice (desktop chip + mobile drawer). Keep paired copies
  // in lockstep so buildFilterUrl()'s dedupe reads consistent state regardless of
  // which copy the user interacted with.
  syncPairedInputs(input) {
    this.container.querySelectorAll('[data-filter-input]').forEach(other => {
      if (other === input) return;
      if (other.name !== input.name || other.type !== input.type) return;
      if (input.type === 'checkbox') {
        if (other.value !== input.value) return;
        other.checked = input.checked;
      } else if (input.type === 'number') {
        other.value = input.value;
      }
    });
  }

  buildFilterUrl() {
    // Start from the current URL so we preserve non-filter params like ?q=
    // (the vendor/type selector for Shopify's virtual /collections/vendors and
    // /collections/types) and anything else the page was loaded with (UTMs,
    // etc). We only overwrite the params we actually own here: filter.*, page,
    // and sort_by.
    const params = new URLSearchParams(window.location.search);
    for (const key of Array.from(params.keys())) {
      if (key.startsWith('filter.') || key === 'page') params.delete(key);
    }
    params.delete('sort_by');

    const seen = new Set();
    this.container.querySelectorAll('[data-filter-input]').forEach(input => {
      // Deduplicate: desktop chips and mobile drawer have duplicate inputs.
      // Checkboxes are keyed by name+value (multiple values share a name).
      // Numbers (price min/max) are keyed by name only — pairs share a name
      // and syncPairedInputs keeps their values aligned.
      const key = input.type === 'number'
        ? `${input.name}|number`
        : `${input.name}|${input.value}|${input.type}`;
      if (seen.has(key)) return;
      seen.add(key);

      if (input.type === 'checkbox' && input.checked) {
        params.append(input.name, input.value);
      } else if (input.type === 'number' && input.value) {
        params.append(input.name, input.value);
      }
    });

    const sortSelect = this.container.querySelector('[data-sort-select]');
    if (sortSelect && sortSelect.value !== 'manual') {
      params.set('sort_by', sortSelect.value);
    }

    const qs = params.toString();
    return `${window.location.pathname}${qs ? '?' + qs : ''}`;
  }

  /* ===== SORT ===== */
  bindSort() {
    const sortSelect = this.container.querySelector('[data-sort-select]');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        const url = this.buildFilterUrl();
        this.fetchAndRender(url);
      });
    }
  }

  /* ===== FETCH AND RENDER (Section Rendering API) ===== */
  async fetchAndRender(url, { pushHistory = true } = {}) {
    // Show skeleton, hide grid
    if (this.skeletonGrid) this.skeletonGrid.hidden = false;
    if (this.productGrid) this.productGrid.style.display = 'none';

    // Update browser URL. Back/Forward has already moved it, and adding an entry
    // here would trap the shopper on this page.
    if (pushHistory) history.pushState({}, '', url);

    try {
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${separator}section_id=${this.sectionId}`);
      const html = await response.text();

      const temp = document.createElement('div');
      temp.innerHTML = html;

      // Replace product grid content
      const newGrid = temp.querySelector('[data-product-grid]');
      if (newGrid && this.productGrid) {
        this.productGrid.innerHTML = newGrid.innerHTML;
      }

      // Replace mobile filter drawer content
      const newFilters = temp.querySelector('[data-filters]');
      const oldFilters = this.container.querySelector('[data-filters]');
      if (newFilters && oldFilters) {
        oldFilters.innerHTML = newFilters.innerHTML;
      }

      // Replace desktop inline filter chips
      const newChips = temp.querySelector('.collection-toolbar__chips');
      const oldChips = this.container.querySelector('.collection-toolbar__chips');
      if (newChips && oldChips) {
        oldChips.innerHTML = newChips.innerHTML;
      }

      // Update product count
      const newCount = temp.querySelector('.collection-toolbar__count');
      const oldCount = this.container.querySelector('.collection-toolbar__count');
      if (newCount && oldCount) {
        oldCount.textContent = newCount.textContent;
      }

      // The dropdown sits outside the replaced regions, so Back would leave it stale
      const newSort = temp.querySelector('[data-sort-select]');
      const oldSort = this.container.querySelector('[data-sort-select]');
      if (newSort && oldSort) {
        oldSort.value = newSort.value;
      }

      // Update active tab state
      const newTabs = temp.querySelectorAll('[data-level-tab]');
      const oldTabs = this.container.querySelectorAll('[data-level-tab]');
      newTabs.forEach((newTab, i) => {
        if (oldTabs[i]) {
          oldTabs[i].className = newTab.className;
          oldTabs[i].setAttribute('aria-selected', newTab.getAttribute('aria-selected'));
        }
      });
    } catch (error) {
      console.error('Collection fetch failed:', error);
    }

    // Hide skeleton, show grid
    if (this.skeletonGrid) this.skeletonGrid.hidden = true;
    if (this.productGrid) this.productGrid.style.display = '';

    // Close all chip dropdowns
    this.closeAllChipDropdowns();

    // Re-bind dynamic elements
    this.bindFilters();
    this.bindFilterChips();
  }

  /* ===== MOBILE FILTER TOGGLE ===== */
  // Runs once: the toggle, backdrop and drawer survive re-renders, so binding them
  // again would make one tap open and instantly close the drawer.
  bindFilterToggle() {
    const toggleBtn = this.container.querySelector('[data-filter-toggle]');
    const filters = document.getElementById('collection-filters');

    if (!filters) return;

    const backdrop = document.getElementById('collection-filters-backdrop');

    const openFilters = () => {
      filters.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
      document.body.classList.add('no-scroll');
    };

    const closeFilters = () => {
      filters.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('no-scroll');
    };

    // Close on backdrop click
    if (backdrop) {
      backdrop.addEventListener('click', closeFilters);
    }

    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const isOpen = filters.classList.contains('is-open');
        if (isOpen) {
          closeFilters();
        } else {
          openFilters();
        }
      });
    }

    // Delegated because the close button is replaced on every re-render
    filters.addEventListener('click', (e) => {
      const isDismiss = e.target === filters || e.target.closest('[data-filter-close]');
      if (isDismiss && filters.classList.contains('is-open')) {
        closeFilters();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (filters.classList.contains('is-open')) {
          closeFilters();
        }
        // Also close any open chip dropdowns
        this.closeAllChipDropdowns();
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new CollectionFiltering());
